import React, { useState, useEffect } from 'react';
import { database } from '../../firebase/config';
import { ref, get, update, onValue, off, runTransaction } from 'firebase/database';
import './WithdrawRequests.css';

const WithdrawRequests = () => {
  // All your existing state
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMode, setFilterMode] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0,
    totalAmount: 0, pendingAmount: 0, approvedAmount: 0, totalTDS: 0, totalNetAmount: 0
  });
  const [tdsSettings, setTdsSettings] = useState({ enabled: false, percentage: 0 });
  const [showTdsModal, setShowTdsModal] = useState(false);
  const [tdsPercentage, setTdsPercentage] = useState('');
  const [tdsPassword, setTdsPassword] = useState('');

  // Utility functions
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(parseFloat(amount || 0));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      approved: '#10b981',
      rejected: '#ef4444',
      cancelled: '#6b7280'
    };
    return colors[status] || '#2196f3';
  };

  // Firebase functions (your existing logic)
  useEffect(() => {
    fetchWithdrawRequests();
    fetchTdsSettings();

    const requestsRef = ref(database, 'HTAMS/transactions');
    const unsubscribe = onValue(requestsRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const requestsArray = Object.entries(data)
          .map(([id, requestData]) => ({
            id,
            ...requestData,
            requestedAt: requestData.requestedAt || requestData.timestamp || Date.now(),
            mode: requestData.mode || requestData.paymentMethod || 'N/A'
          }))
          .filter(request => request.type === 'withdrawal')
          .sort((a, b) => b.requestedAt - a.requestedAt);

        // Fetch user details for each request
        const requestsWithUserDetails = await Promise.all(
          requestsArray.map(async (request) => {
            if (request.userId && !request.userDetails) {
              try {
                const userRef = ref(database, `HTAMS/users/${request.userId}`);
                const userSnapshot = await get(userRef);
                if (userSnapshot.exists()) {
                  const userData = userSnapshot.val();
                  return {
                    ...request,
                    userDetails: {
                      name: userData.name || userData.Name || 'N/A',
                      email: userData.email || userData.Email || 'N/A',
                      mobile: userData.mobile || userData.Mobile || userData.phone || userData.Phone || userData.phoneNumber || 'N/A'
                    }
                  };
                }
              } catch (error) {
                console.error(`Error fetching user details for ${request.userId}:`, error);
              }
            }
            return request;
          })
        );

        setRequests(requestsWithUserDetails);
        calculateStats(requestsWithUserDetails);
      } else {
        setRequests([]);
        setStats({ total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0, totalAmount: 0, pendingAmount: 0, approvedAmount: 0, totalTDS: 0, totalNetAmount: 0 });
      }
      setLoading(false);
    });

    return () => off(requestsRef, 'value', unsubscribe);
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, filterStatus, filterMode, searchTerm, dateFilter]);

  const fetchWithdrawRequests = async () => {
    try {
      setLoading(true);
      const requestsRef = ref(database, 'HTAMS/transactions');
      const snapshot = await get(requestsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const requestsArray = Object.entries(data)
          .map(([id, requestData]) => ({
            id,
            ...requestData,
            requestedAt: requestData.requestedAt || requestData.timestamp || Date.now(),
            mode: requestData.mode || requestData.paymentMethod || 'N/A'
          }))
          .filter(request => request.type === 'withdrawal')
          .sort((a, b) => b.requestedAt - a.requestedAt);

        // Fetch user details for each request
        const requestsWithUserDetails = await Promise.all(
          requestsArray.map(async (request) => {
            if (request.userId && !request.userDetails) {
              try {
                const userRef = ref(database, `HTAMS/users/${request.userId}`);
                const userSnapshot = await get(userRef);
                if (userSnapshot.exists()) {
                  const userData = userSnapshot.val();
                  return {
                    ...request,
                    userDetails: {
                      name: userData.name || userData.Name || 'N/A',
                      email: userData.email || userData.Email || 'N/A',
                      mobile: userData.mobile || userData.Mobile || userData.phone || userData.Phone || userData.phoneNumber || 'N/A'
                    }
                  };
                }
              } catch (error) {
                console.error(`Error fetching user details for ${request.userId}:`, error);
              }
            }
            return request;
          })
        );

        setRequests(requestsWithUserDetails);
        calculateStats(requestsWithUserDetails);
      }
    } catch (error) {
      console.error('Error fetching withdraw requests:', error);
      alert('Error loading withdraw requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (requestsArray) => {
    const totalTDS = requestsArray
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + parseFloat(r.tdsAmount || 0), 0);
    const totalNetAmount = requestsArray
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + parseFloat(r.netAmount || r.amount), 0);

    const stats = {
      total: requestsArray.length,
      pending: requestsArray.filter(r => r.status === 'pending').length,
      approved: requestsArray.filter(r => r.status === 'approved').length,
      rejected: requestsArray.filter(r => r.status === 'rejected').length,
      cancelled: requestsArray.filter(r => r.status === 'cancelled').length,
      totalAmount: requestsArray.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
      pendingAmount: requestsArray.filter(r => r.status === 'pending').reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
      approvedAmount: requestsArray.filter(r => r.status === 'approved').reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
      totalTDS,
      totalNetAmount
    };
    setStats(stats);
  };

  const fetchTdsSettings = async () => {
    try {
      const tdsRef = ref(database, 'HTAMS/settings/tds');
      const snapshot = await get(tdsRef);
      if (snapshot.exists()) {
        setTdsSettings(snapshot.val());
      }
    } catch (error) {
      console.error('Error fetching TDS settings:', error);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (filterStatus !== 'all') filtered = filtered.filter(r => r.status === filterStatus);
    if (filterMode !== 'all') filtered = filtered.filter(r => r.mode === filterMode);

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.userDetails?.name?.toLowerCase().includes(searchLower) ||
        r.userDetails?.email?.toLowerCase().includes(searchLower) ||
        r.userDetails?.mobile?.includes(searchTerm) ||
        r.id.toLowerCase().includes(searchLower) ||
        r.amount?.toString().includes(searchTerm)
      );
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filtered = filtered.filter(r => {
        const requestDate = new Date(r.requestedAt);
        switch (dateFilter) {
          case 'today': return requestDate >= today;
          case 'yesterday': {
            const yesterday = new Date(today.getTime() - 86400000);
            return requestDate >= yesterday && requestDate < today;
          }
          case 'week': {
            const weekAgo = new Date(today.getTime() - 7 * 86400000);
            return requestDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today.getTime() - 30 * 86400000);
            return requestDate >= monthAgo;
          }
          default: return true;
        }
      });
    }

    setFilteredRequests(filtered);
  };

  const approveRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this withdrawal request?')) return;

    setIsProcessing(true);
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) return alert('Request not found');

      const userId = request.userId;
      const updates = {
        status: 'approved',
        approvedAt: Date.now(),
        approvedBy: 'admin',
        processedAt: Date.now()
      };

      await update(ref(database, `HTAMS/transactions/${requestId}`), updates);
      await update(ref(database, `HTAMS/users/${userId}/transactions/${requestId}`), updates);

      alert(`Withdrawal request approved successfully! ${formatCurrency(request.netAmount || request.amount)}`);
      setShowModal(false);
      fetchWithdrawRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const rejectRequest = async (requestId) => {
    const rejectReason = window.prompt('Please enter rejection reason (optional)', 'No reason provided');
    if (!window.confirm('Are you sure you want to reject this withdrawal request?')) return;

    setIsProcessing(true);
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) return alert('Request not found');

      const amount = parseFloat(request.amount);
      const userId = request.userId;
      const updates = {
        status: 'rejected',
        rejectedAt: Date.now(),
        rejectedBy: 'admin',
        rejectionReason: rejectReason,
        processedAt: Date.now(),
        refundedToWallet: true
      };

      await update(ref(database, `HTAMS/transactions/${requestId}`), updates);
      await update(ref(database, `HTAMS/users/${userId}/transactions/${requestId}`), updates);

      const userRef = ref(database, `HTAMS/users/${userId}`);
      await runTransaction(userRef, (userData) => {
        if (userData) {
          const currentMySales = parseFloat(userData.MySales || 0);
          userData.MySales = currentMySales + amount;
          userData.lastUpdated = Date.now();
        }
        return userData;
      });

      alert(`${formatCurrency(amount)} has been refunded to user\'s wallet.`);
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const exportToCSV = () => {
    // Essential headers only
    const headers = [
      'Transaction ID',
      'User Name',
      'Email',
      'Mobile',
      'Gross Amount (₹)',
      'TDS %',
      'TDS Amount (₹)',
      'Net Amount (₹)',
      'Mode',
      'Status',
      'Requested Date',
      'Requested Time',
      'Bank Account Holder',
      'Bank Account Number',
      'Bank IFSC Code',
      'Bank Name',
      'Bank Branch'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredRequests.map(r => {
        // Format amounts with proper currency highlighting
        const grossAmount = `₹${parseFloat(r.amount || 0).toLocaleString('en-IN')}`;
        const tdsAmount = r.tdsAmount ? `₹${parseFloat(r.tdsAmount).toLocaleString('en-IN')}` : '₹0';
        const netAmount = `₹${parseFloat(r.netAmount || r.amount || 0).toLocaleString('en-IN')}`;

        return [
          r.id || '',
          `"${r.userDetails?.name || 'N/A'}"`,
          r.userDetails?.email || 'N/A',
          r.userDetails?.mobile || 'N/A',
          grossAmount,
          r.tdsPercentage || 0,
          tdsAmount,
          netAmount,
          (r.mode || 'N/A').toUpperCase(),
          (r.status || 'N/A').toUpperCase(),
          formatDate(r.requestedAt) || 'N/A',
          formatTime(r.requestedAt) || 'N/A',
          `"${r.bankDetails?.accountHolderName || 'N/A'}"`,
          r.bankDetails?.accountNumber || 'N/A',
          r.bankDetails?.ifscCode || 'N/A',
          `"${r.bankDetails?.bankName || 'N/A'}"`,
          `"${r.bankDetails?.branch || 'N/A'}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    a.download = `Withdrawal-Requests-${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    // Show success message
    alert(`✅ Export successful!\n\nTotal Records: ${filteredRequests.length}\nFile: Withdrawal-Requests-${timestamp}.csv`);
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterMode('all');
    setSearchTerm('');
    setDateFilter('all');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading withdraw requests...</p>
      </div>
    );
  }

  return (
    <div className="withdraw-requests-container">
      {/* Header */}
      <div className="header-section">
        <div className="header-content">
          <div className="header-main">
            <h1>Withdraw Requests</h1>
            <p className="header-subtitle">Manage and process withdrawal requests efficiently</p>
          </div>
          <div className="header-actions">
            {/* <button className="btn btn-primary" onClick={() => setShowTdsModal(true)}>
              TDS Settings {tdsSettings.enabled && `(${tdsSettings.percentage}%)`}
            </button> */}
            <button className="btn btn-success" onClick={exportToCSV}>
              Export CSV
            </button>
            <button className="btn btn-secondary" onClick={fetchWithdrawRequests}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card total">

          <div className="stat-content">
            <h3>Total Requests</h3>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-subtext">{formatCurrency(stats.totalAmount)}</div>
          </div>
        </div>
        <div className="stat-card pending">

          <div className="stat-content">
            <h3>Pending</h3>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-subtext">{formatCurrency(stats.pendingAmount)}</div>
          </div>
        </div>
        <div className="stat-card approved">

          <div className="stat-content">
            <h3>Approved</h3>
            <div className="stat-value">{stats.approved}</div>
            <div className="stat-subtext">{formatCurrency(stats.approvedAmount)}</div>
          </div>
        </div>
        <div className="stat-card rejected">

          <div className="stat-content">
            <h3>Rejected</h3>
            <div className="stat-value">{stats.rejected}</div>
          </div>
        </div>
        <div className="stat-card cancelled">

          <div className="stat-content">
            <h3>Cancelled</h3>
            <div className="stat-value">{stats.cancelled}</div>
          </div>
        </div>
        <div className="stat-card tds">

          <div className="stat-content">
            <h3>Total TDS</h3>
            <div className="stat-value">{formatCurrency(stats.totalTDS)}</div>
            <div className="stat-subtext">Tax Deducted</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-header">
          <div className="filters-title">Filters</div>
          <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear All</button>
        </div>
        <div className="filters-grid">
          <div className="form-group">
            <label>Status</label>
            <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="form-group">
            <label>Mode</label>
            <select className="form-select" value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
              <option value="all">All Modes</option>
              <option value="bank">Bank Transfer</option>
              <option value="upi">UPI</option>
            </select>
          </div>
          <div className="form-group">
            <label>Date</label>
            <select className="form-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search by name, email, mobile, ID, amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        Showing <strong>{filteredRequests.length}</strong> of <strong>{requests.length}</strong> requests
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>User Details</th>
              <th>Gross Amount</th>
              <th>TDS</th>
              <th>Net Amount</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Requested Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state">
                  <div className="empty-icon">📭</div>
                  <p>No withdrawal requests found</p>
                  <p style={{ color: 'var(--gray-500)' }}>Try adjusting your filters</p>
                </td>
              </tr>
            ) : (
              filteredRequests.map(request => (
                <tr key={request.id}>
                  <td><span className="id-badge">{request.id}</span></td>
                  <td>
                    <div className="user-details">
                      <div className="user-name">{request.userDetails?.name || 'N/A'}</div>
                      <div className="user-email">{request.userDetails?.email || 'N/A'}</div>
                      <div className="user-mobile">{request.userDetails?.mobile || 'N/A'}</div>
                    </div>
                  </td>
                  <td><div className="amount">{formatCurrency(request.amount)}</div></td>
                  <td>
                    <div className="tds-info">
                      {request.tdsApplied ? (
                        <>
                          <div className="tds-percentage">{request.tdsPercentage}%</div>
                          <div className="tds-amount">{formatCurrency(request.tdsAmount)}</div>
                        </>
                      ) : (
                        <span className="no-tds">N/A</span>
                      )}
                    </div>
                  </td>
                  <td><div className="net-amount">{formatCurrency(request.netAmount || request.amount)}</div></td>
                  <td><div className="mode-badge">{request.mode?.toUpperCase() || 'N/A'}</div></td>
                  <td>
                    <span
                      className={`status-badge ${request.status}`}
                      style={{ backgroundColor: getStatusColor(request.status) }}
                    >
                      {request.status?.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="date">
                      <div className="date-text">{formatDate(request.requestedAt)}</div>
                      <small className="time-text">{formatTime(request.requestedAt)}</small>
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-sm view" onClick={() => { setSelectedRequest(request); setShowModal(true); }}>
                        View
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button className="btn btn-sm approve" onClick={() => approveRequest(request.id)} disabled={isProcessing}>
                            {isProcessing ? 'Processing...' : 'Approve'}
                          </button>
                          <button className="btn btn-sm reject" onClick={() => rejectRequest(request.id)} disabled={isProcessing}>
                            {isProcessing ? 'Processing...' : 'Reject'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Request Details Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: '760px', height: '85%' }}>
            <div className="modal-header">
              <h3 className="modal-title">Withdrawal Request Details</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                {/* User Details Section */}
                <div className="details-section">
                  <h4>👤 User Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedRequest.userDetails?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedRequest.userDetails?.email || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Mobile</span>
                    <span className="detail-value">{selectedRequest.userDetails?.mobile || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User ID</span>
                    <span className="detail-value">{selectedRequest.userId || 'N/A'}</span>
                  </div>
                </div>

                {/* Transaction Information Section */}
                <div className="details-section">
                  <h4>💰 Transaction Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Transaction ID</span>
                    <span className="detail-value">{selectedRequest.id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Gross Amount</span>
                    <span className="detail-value amount-highlight">{formatCurrency(selectedRequest.amount)}</span>
                  </div>
                  {selectedRequest.tdsApplied && (
                    <>
                      <div className="detail-row">
                        <span className="detail-label">TDS ({selectedRequest.tdsPercentage}%)</span>
                        <span className="detail-value" style={{ color: '#ef4444' }}>
                          - {formatCurrency(selectedRequest.tdsAmount)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Net Amount</span>
                        <span className="detail-value amount-highlight" style={{ color: '#10b981', fontWeight: 'bold' }}>
                          {formatCurrency(selectedRequest.netAmount || selectedRequest.amount)}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Mode</span>
                    <span className="detail-value">
                      <div className="mode-badge">{selectedRequest.mode?.toUpperCase() || 'N/A'}</div>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className="detail-value">
                      <span className={`status-badge ${selectedRequest.status}`} style={{ backgroundColor: getStatusColor(selectedRequest.status) }}>
                        {selectedRequest.status?.toUpperCase()}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Bank/UPI Details Section */}
                {selectedRequest.mode === 'bank' && selectedRequest.bankDetails && (
                  <div className="details-section">
                    <h4>🏦 Bank Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">Account Holder Name</span>
                      <span className="detail-value">{selectedRequest.bankDetails.accountHolderName || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Account Number</span>
                      <span className="detail-value">{selectedRequest.bankDetails.accountNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">IFSC Code</span>
                      <span className="detail-value">{selectedRequest.bankDetails.ifscCode || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Bank Name</span>
                      <span className="detail-value">{selectedRequest.bankDetails.bankName || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Branch</span>
                      <span className="detail-value">{selectedRequest.bankDetails.branch || 'N/A'}</span>
                    </div>
                  </div>
                )}

                {selectedRequest.mode === 'upi' && selectedRequest.upiDetails && (
                  <div className="details-section">
                    <h4>📱 UPI Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">UPI ID</span>
                      <span className="detail-value">{selectedRequest.upiDetails.upiId || selectedRequest.upiId || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">UPI Name</span>
                      <span className="detail-value">{selectedRequest.upiDetails.upiName || 'N/A'}</span>
                    </div>
                  </div>
                )}

                {/* Date & Time Information */}
                <div className="details-section">
                  <h4>📅 Date & Time Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Requested Date</span>
                    <span className="detail-value">
                      {formatDate(selectedRequest.requestedAt)} at {formatTime(selectedRequest.requestedAt)}
                    </span>
                  </div>
                  {selectedRequest.approvedAt && (
                    <div className="detail-row">
                      <span className="detail-label">Approved Date</span>
                      <span className="detail-value" style={{ color: '#10b981' }}>
                        {formatDate(selectedRequest.approvedAt)} at {formatTime(selectedRequest.approvedAt)}
                      </span>
                    </div>
                  )}
                  {selectedRequest.rejectedAt && (
                    <div className="detail-row">
                      <span className="detail-label">Rejected Date</span>
                      <span className="detail-value" style={{ color: '#ef4444' }}>
                        {formatDate(selectedRequest.rejectedAt)} at {formatTime(selectedRequest.rejectedAt)}
                      </span>
                    </div>
                  )}
                  {selectedRequest.processedAt && (
                    <div className="detail-row">
                      <span className="detail-label">Processed Date</span>
                      <span className="detail-value">
                        {formatDate(selectedRequest.processedAt)} at {formatTime(selectedRequest.processedAt)}
                      </span>
                    </div>
                  )}
                  {selectedRequest.approvedBy && (
                    <div className="detail-row">
                      <span className="detail-label">Approved By</span>
                      <span className="detail-value">{selectedRequest.approvedBy}</span>
                    </div>
                  )}
                  {selectedRequest.rejectedBy && (
                    <div className="detail-row">
                      <span className="detail-label">Rejected By</span>
                      <span className="detail-value">{selectedRequest.rejectedBy}</span>
                    </div>
                  )}
                </div>

                {/* Additional Information */}
                {(selectedRequest.rejectionReason || selectedRequest.note || selectedRequest.remarks) && (
                  <div className="details-section">
                    <h4>📝 Additional Information</h4>
                    {selectedRequest.rejectionReason && (
                      <div className="detail-row">
                        <span className="detail-label">Rejection Reason</span>
                        <span className="detail-value" style={{ color: '#ef4444' }}>
                          {selectedRequest.rejectionReason}
                        </span>
                      </div>
                    )}
                    {selectedRequest.note && (
                      <div className="detail-row">
                        <span className="detail-label">Note</span>
                        <span className="detail-value">{selectedRequest.note}</span>
                      </div>
                    )}
                    {selectedRequest.remarks && (
                      <div className="detail-row">
                        <span className="detail-label">Remarks</span>
                        <span className="detail-value">{selectedRequest.remarks}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                <button className="btn btn-sm cancel" onClick={() => setShowModal(false)}>Close</button>
                {selectedRequest.status === 'pending' && (
                  <>
                    <button className="btn btn-sm approve" onClick={() => approveRequest(selectedRequest.id)} disabled={isProcessing}>
                      {isProcessing ? 'Processing...' : 'Approve Request'}
                    </button>
                    <button className="btn btn-sm reject" onClick={() => rejectRequest(selectedRequest.id)} disabled={isProcessing}>
                      {isProcessing ? 'Processing...' : 'Reject Request'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TDS Settings Modal */}
      {showTdsModal && (
        <div className="modal-overlay tds-modal" onClick={() => setShowTdsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">TDS Settings</h3>
              <button className="modal-close" onClick={() => setShowTdsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-section form-info">
                <p><strong>Note:</strong> TDS will be automatically deducted from all approved withdrawal requests.</p>
              </div>
              <div className="form-section">
                <div className="form-group">
                  <label>TDS Percentage</label>
                  <input
                    type="number"
                    className="form-input"
                    value={tdsPercentage}
                    onChange={(e) => setTdsPercentage(e.target.value)}
                    placeholder="Enter TDS percentage (0-100)"
                    min="0" max="100" step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Admin Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={tdsPassword}
                    onChange={(e) => setTdsPassword(e.target.value)}
                    placeholder="Enter admin password"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowTdsModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (tdsPassword !== 'admin123') {
                      alert('Incorrect password!');
                      return;
                    }
                    alert('TDS settings saved!');
                    setShowTdsModal(false);
                  }}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WithdrawRequests;
